import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { KeycloakAuthService } from './keycloak-auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptorService implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  refreshUrl: string = environment.sbmsUrl + "/authorization/refresh";
  private refreshTokenObs: Observable<any> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private keycloakService: KeycloakAuthService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    //if unsafe request, set X-XSRF header
    if (this.isUnsafeMethod(req)) {
      let token = this.getCookieValue("XSRF-TOKEN", document.cookie);
      if (token) {
        req = req.clone({
          setHeaders: {
            "X-XSRF-TOKEN": token
          }
        });
      }
    }
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          console.log("error 401 catched");
          return this.handle401Error(req, next);
        } else if (err.status === 400 || req.url === this.refreshUrl) {
          console.log("echec lors du refresh token", err);
          this.isRefreshing = false;
          this.refreshTokenObs = null;
          this.refreshTokenSubject.next(false);
          //TODO: logout, clear localstorage and CSRF token ?
          localStorage.removeItem("isConnected");
          this.keycloakService.logout();
          document.cookie = "XSRF-TOKEN=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
          this.router.navigate(["/post-logout"]);

          return throwError(() => new Error("Refresh token expiré"));
        }
        return throwError(() => err);
      })
    );
  }

  isUnsafeMethod(req: HttpRequest<any>) {
    const methodName = req.method.toUpperCase();
    if (methodName === "POST" || methodName === "PUT" || methodName === "DELETE" || methodName === "PATCH") {
      return true;
    }
    return false;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      if (!this.refreshTokenObs) {
        let headers = new HttpHeaders();
        const tokenvalue = this.getCookieValue("XSRF-TOKEN", document.cookie);
        if (tokenvalue) {
          headers = headers.set("X-XSRF-TOKEN", tokenvalue);
        }
        this.refreshTokenObs = this.http.post(this.refreshUrl, {}, { headers: headers, withCredentials: true }).pipe(
          switchMap(() => {
            console.log("token refresh done");
            this.isRefreshing = false;
            this.refreshTokenObs = null;
            this.refreshTokenSubject.next(true);
            return next.handle(request);
          })),
          catchError((err) => {
            console.log("echec lors du refresh token", err);
            this.isRefreshing = false;
            this.refreshTokenObs = null;
            this.refreshTokenSubject.next(false);
            this.router.navigate(["/login-fail"]);
            return throwError(() => new Error("Refresh token expiré"));
          });
      }
    } else {
      console.log("isRefreshing is true");
      //Queue the requests until the refresh is done
      return this.refreshTokenSubject.pipe(
        filter((status) => status != null),
        take(1),
        switchMap((status) => {
          if (status === true) {
            return next.handle(request);
          } else {
            return throwError(() => new Error("Token refresh failed"));
          }
        }),
        catchError((error) => {
          console.error("Refresh request failed, redirecting to login", error);
          this.router.navigate(["/login-fail"]);
          return throwError(() => error);
        })
      );
    }
    return this.refreshTokenObs;
  }

  getCookieValue(cookieName: string, cookieStr: string): string | null {
    const cookies = cookieStr.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === cookieName) {
        return value;
      }
    }
    return null;
  }
}
